import { withErrorBoundary } from 'Components/Core/ErrorBoundary';
import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { ChangeEventWithData } from 'Components/Core/types';
import { StandaloneEquipmentDropdownField } from 'Components/List';
import { IWithWorksheetInjectedProps, WorksheetRecordGrid } from 'Components/Worksheets/Core';
import { RootState } from 'Data/Redux';
import { hasNonEmptyValue, hasValue } from 'Lib/Any';
import { DateTimeDataFormat } from 'Lib/Format';
import { useHasChanged } from 'Lib/Hooks';
import { pipe } from 'Lib/Hooks/Selectors';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  ButtonGroup,
  Checkbox,
  DateInput,
  DateTimeInput,
  Field,
  NumericInput,
  RadioGroup,
  TextOutput,
  TimeInput,
} from '../../Core';
import { GridFieldSection, ListFieldSection, PageSection, SectionGroup } from '../../Layout';

type LabOption = 'always' | 'optional' | 'never';

let DryMassEntryListSectionTest: React.FC<IWithWorksheetInjectedProps> = ({
  createWorksheetSelector,
  addArrayEntry,
  removeArrayEntry,
}) => {
  const [labOption, setLabOption] = useState<LabOption>('optional');
  const [gridOrientation, setGridOrientation] = useState<'horizontal' | 'vertical'>('vertical');
  const [oneReadingDisplay, setOneReadingDisplay] = useState<'grid' | 'list'>('grid');
  const homeLabId = useSelector((state: RootState) => state.user?.data?.HomeLaboratory?.Id);
  const dryMassReadings = useSelector(pipe(createWorksheetSelector!('DryMassReadings'), (r) => r?.length ?? 0));
  const [requireDryMassEntryBeforeAdding, setRequireDryMassEntryBeforeAdding] = useState(false);
  const [syncingReadings, setSyncingReadings] = useState(false);
  // TODO: Make it so we can get the grid to work this out from the records instead of selecting in the parent
  const lastDryMassEntered = useSelector(
    pipe(createWorksheetSelector!('DryMassReadings'), (r) =>
      hasNonEmptyValue(r) ? hasValue(r[r.length - 1]?.['TareAndMaterialDryMass']) : false
    )
  );
  // Ensure at least one/two readings exist
  // (All this garbage is here to mock back-end syncing)
  const optionChanged = useHasChanged(labOption);

  useEffect(() => {
    if (optionChanged) setSyncingReadings(true);
  }, [optionChanged]);

  const dryMassReadingCount = useSelector(pipe(createWorksheetSelector!('DryMassReadings'), (r) => r?.length ?? 0));
  const targetReadings = labOption === 'always' ? 2 : 1;

  useEffect(() => {
    if (syncingReadings) {
      if (dryMassReadingCount < targetReadings) {
        addArrayEntry!({
          name: 'DryMassReadings',
          index: dryMassReadingCount,
          sync: true,
          data: undefined,
        });
      } else if (dryMassReadingCount > targetReadings) {
        removeArrayEntry!({
          name: 'DryMassReadings',
          index: dryMassReadingCount - 1,
          sync: true,
          data: undefined,
        });
      } else {
        setSyncingReadings(false);
      }
    }
  }, [syncingReadings, targetReadings, dryMassReadingCount, addArrayEntry, removeArrayEntry]);

  return (
    <>
      <PageSection heading="Options">
        <ListFieldSection>
          <Field
            label="Laboratory Option: Enter Drying to Constant Mass"
            input={<RadioGroup />}
            options={[
              { label: 'Always', value: 'always' },
              { label: 'Optional', value: 'optional' },
              { label: 'Never', value: 'never' },
            ]}
            value={labOption}
            onChange={(e: ChangeEventWithData) => setLabOption(e.data?.value)}
          />
          <Field
            label="Require Last Dry Mass to be Entered to Add New Entry"
            input={<Checkbox />}
            value={requireDryMassEntryBeforeAdding}
            onChange={(e: ChangeEventWithData) => setRequireDryMassEntryBeforeAdding(e.data?.value)}
            disabled={labOption === 'never'}
          />
          <Field
            label="Single Reading Displayed as"
            input={<RadioGroup />}
            options={[
              { label: 'List Section', value: 'list' },
              { label: 'Grid', value: 'grid' },
            ]}
            value={oneReadingDisplay}
            onChange={(e: ChangeEventWithData) => setOneReadingDisplay(e.data?.value)}
            disabled={labOption === 'never'}
          />
          <Field
            label="Grid Orientation"
            input={<RadioGroup />}
            options={[
              { label: 'Horizontal', value: 'horizontal' },
              { label: 'Vertical', value: 'vertical' },
            ]}
            value={gridOrientation}
            onChange={(e: ChangeEventWithData) => setGridOrientation(e.data?.value)}
          />
        </ListFieldSection>
      </PageSection>
      <PageSection heading="Dry Mass Entry - Moisture Content in List Section">
        <SectionGroup>
          <ListFieldSection>
            <Field name="TareAndMaterialWetMass" input={<NumericInput />} />
            <Field name="MaterialWetMass" input={<TextOutput />} value="Calculated" />
            {labOption !== 'never' && <Field name="DateTimeInOven" input={<DateTimeInput />} />}
          </ListFieldSection>
          <ListFieldSection>
            <StandaloneEquipmentDropdownField
              name="DryMassBalance"
              equipmentType="balance"
              laboratoryId={homeLabId}
              disabled={true}
            />
          </ListFieldSection>
          {dryMassReadings <= 1 && (oneReadingDisplay === 'list' || labOption === 'never') ? (
            <NamePrefixModifier appendNamePrefix={'DryMassReadings[0]'}>
              {labOption !== 'never' && (
                <ListFieldSection>
                  <Field name="DateTimeOutOfOven" input={<DateTimeInput />} />
                  <Field name="DryingTime" input={<TextOutput />} value="Calculated" />
                </ListFieldSection>
              )}
              <ListFieldSection>
                <Field name="TareAndMaterialDryMass" input={<NumericInput />} />
                <Field name="MaterialDryMass" input={<TextOutput />} value="Calculated" />
              </ListFieldSection>
              {labOption !== 'never' && (
                <ButtonGroup>
                  <Button
                    onClick={() =>
                      addArrayEntry!({
                        name: 'DryMassReadings',
                        sync: true,
                        data: undefined,
                        index: dryMassReadings,
                      })
                    }
                    disabled={requireDryMassEntryBeforeAdding && !lastDryMassEntered}
                  >
                    Dry Again
                  </Button>
                </ButtonGroup>
              )}
            </NamePrefixModifier>
          ) : (
            <>
              <GridFieldSection>
                <WorksheetRecordGrid
                  name="DryMassReadings"
                  orientation={gridOrientation as 'horizontal' | 'vertical'}
                  recordProps={{
                    enforceAtLeastOneRecord: true,
                    preventRemove: 'all-but-last',
                    preventCreate: labOption === 'never' || (requireDryMassEntryBeforeAdding && !lastDryMassEntered),
                    removeConfirm: {
                      confirm: true,
                      confirmTitle: 'Delete Dry Mass Reading',
                      confirmText: 'Are you sure you want to delete the dry mass reading? Data cannot be recovered.',
                      confirmButtonText: 'Delete',
                    },
                  }}
                  fieldSet={[
                    ...(labOption !== 'never'
                      ? [
                          {
                            name: 'DateTimeOutOfOven',
                            component: (
                              <Field input={<DateInput />} dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime} />
                            ),
                            label: 'Date Out of Oven',
                          },
                          {
                            name: 'DateTimeOutOfOven',
                            component: (
                              <Field input={<TimeInput />} dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime} />
                            ),

                            label: 'Time Out of Oven',
                          },
                          {
                            name: 'DryingTime',
                            component: <Field input={<TextOutput />} value={'Calculated'} />,
                          },
                        ]
                      : []),
                    {
                      name: 'TareAndMaterialDryMass',
                      component: <Field input={<NumericInput />} />,
                      label: 'Tare & Dry Mass (g)',
                    },
                  ]}
                />
              </GridFieldSection>

              <ListFieldSection>
                <Field label="Material Dry Mass (g)" input={<TextOutput />} value="Calculated" />
              </ListFieldSection>
            </>
          )}
        </SectionGroup>
      </PageSection>
    </>
  );
};

DryMassEntryListSectionTest = withErrorBoundary(withInjectedProps(DryMassEntryListSectionTest));

export default DryMassEntryListSectionTest;
export { DryMassEntryListSectionTest };
